/**
 * Dashboard Stat Widgets
 * Extracted from HubDashboard.jsx
 * These widgets display various dashboard statistics
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ClipboardList, Truck, BarChart3, Users, Shield,
  FileCheck, Award, Zap, Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom hook for dashboard stats
function useDashboardStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/stats`, { withCredentials: true });
        setStats(res.data);
      } catch (err) {
        console.error('Failed to fetch dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, []);

  return { stats, loading };
}

export function TasksWidget() {
  const { stats, loading } = useDashboardStats();
  
  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full" data-testid="tasks-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-blue-400" />
          </div>
          <h3 className="font-bold">Training Progress</h3>
        </div>
        <span className="text-xs bg-blue-500/20 text-blue-400 px-2.5 py-1 rounded-full font-medium">
          {loading ? '...' : `${stats?.training?.pending || 0} pending`}
        </span>
      </div>
      <div className="space-y-3">
        {loading ? (
          <div className="animate-pulse space-y-2">
            <div className="h-12 bg-secondary/40 rounded-xl"></div>
            <div className="h-12 bg-secondary/40 rounded-xl"></div>
          </div>
        ) : (
          <>
            <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Completed</span>
                <span className="text-green-400 font-bold">{stats?.training?.completed || 0}</span>
              </div>
              <div className="h-2 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${stats?.training?.completion_rate || 0}%` }} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">{stats?.training?.completion_rate || 0}% completion rate</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-center">
                <p className="text-lg font-bold text-yellow-400">{stats?.training?.pending || 0}</p>
                <p className="text-xs text-yellow-400/70">Pending</p>
              </div>
              <div className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/20 text-center">
                <p className="text-lg font-bold text-purple-400">{stats?.training?.total || 0}</p>
                <p className="text-xs text-purple-400/70">Total Sent</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function TrucksWidget() {
  const { stats, loading } = useDashboardStats();
  
  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full" data-testid="trucks-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <Truck className="w-5 h-5 text-amber-400" />
          </div>
          <h3 className="font-bold">Fleet Overview</h3>
        </div>
        <span className="text-xs bg-amber-500/20 text-amber-400 px-2.5 py-1 rounded-full font-medium">
          {loading ? '...' : `${stats?.fleet?.total_trucks || 0} units`}
        </span>
      </div>
      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-16 bg-secondary/40 rounded-xl"></div>
          <div className="h-16 bg-secondary/40 rounded-xl"></div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20 text-center">
              <p className="text-2xl font-bold text-green-400">{stats?.fleet?.active_trucks || 0}</p>
              <p className="text-xs text-green-400/70">Active Trucks</p>
            </div>
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-center">
              <p className="text-2xl font-bold text-amber-400">{stats?.fleet?.total_trucks || 0}</p>
              <p className="text-xs text-amber-400/70">Total Fleet</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-center">
              <p className="text-2xl font-bold text-blue-400">{stats?.fleet?.active_drivers || 0}</p>
              <p className="text-xs text-blue-400/70">Active Drivers</p>
            </div>
            <div className="p-3 rounded-xl bg-purple-500/10 border border-purple-500/20 text-center">
              <p className="text-2xl font-bold text-purple-400">{stats?.fleet?.total_drivers || 0}</p>
              <p className="text-xs text-purple-400/70">Total Drivers</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function StatsWidget() {
  const { stats, loading } = useDashboardStats();
  
  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full" data-testid="stats-widget">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-purple-400" />
        </div>
        <h3 className="font-bold">Quick Stats</h3>
      </div>
      {loading ? (
        <div className="animate-pulse">
          <div className="grid grid-cols-3 gap-3">
            <div className="h-16 bg-secondary/40 rounded-xl"></div>
            <div className="h-16 bg-secondary/40 rounded-xl"></div>
            <div className="h-16 bg-secondary/40 rounded-xl"></div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 bg-pink-500/15 rounded-xl border border-pink-500/20">
            <p className="text-xl font-bold text-pink-400">{stats?.social?.total_kudos || 0}</p>
            <p className="text-xs text-pink-400/70">Total Kudos</p>
          </div>
          <div className="text-center p-3 bg-blue-500/15 rounded-xl border border-blue-500/20">
            <p className="text-xl font-bold text-blue-400">{stats?.social?.unread_messages || 0}</p>
            <p className="text-xs text-blue-400/70">Unread</p>
          </div>
          <div className="text-center p-3 bg-green-500/15 rounded-xl border border-green-500/20">
            <p className="text-xl font-bold text-green-400">{stats?.social?.upcoming_events || 0}</p>
            <p className="text-xs text-green-400/70">Events</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function DriversWidget() {
  const { stats, loading } = useDashboardStats();
  
  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full" data-testid="drivers-widget">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
          <Users className="w-5 h-5 text-green-400" />
        </div>
        <h3 className="font-bold">Driver Stats</h3>
      </div>
      {loading ? (
        <div className="animate-pulse">
          <div className="grid grid-cols-2 gap-3">
            <div className="h-16 bg-secondary/40 rounded-xl"></div>
            <div className="h-16 bg-secondary/40 rounded-xl"></div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-3 bg-green-500/15 rounded-xl border border-green-500/20">
              <p className="text-2xl font-bold text-green-400">{stats?.fleet?.active_drivers || 0}</p>
              <p className="text-xs text-green-400/70">Active</p>
            </div>
            <div className="text-center p-3 bg-secondary/30 rounded-xl">
              <p className="text-2xl font-bold">{stats?.fleet?.total_drivers || 0}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </div>
          </div>
          {stats?.recent_kudos?.length > 0 && (
            <div className="p-2 rounded-lg bg-pink-500/10 border border-pink-500/20">
              <p className="text-xs text-pink-400/70 mb-1">Recent Recognition</p>
              <p className="text-sm truncate">
                <span className="font-medium">{stats.recent_kudos[0]?.recipient_name}</span>
                <span className="text-muted-foreground"> - {stats.recent_kudos[0]?.category}</span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function SafetyWidget() {
  const { stats, loading } = useDashboardStats();
  
  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full" data-testid="safety-widget">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
          <Shield className="w-5 h-5 text-red-400" />
        </div>
        <h3 className="font-bold">Announcements</h3>
      </div>
      {loading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-16 bg-secondary/40 rounded-xl"></div>
          <div className="h-16 bg-secondary/40 rounded-xl"></div>
        </div>
      ) : (
        <div className="space-y-2">
          {stats?.recent_announcements?.length > 0 ? (
            stats.recent_announcements.map((ann, idx) => (
              <div key={idx} className="p-3 rounded-xl bg-secondary/40 border-l-4 border-l-accent">
                <p className="text-sm font-medium line-clamp-2">{ann.title || ann.content?.slice(0, 50)}</p>
                <span className="text-xs text-muted-foreground">{ann.author_name}</span>
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-muted-foreground text-sm">
              No recent announcements
            </div>
          )}
          <div className="text-center pt-2">
            <span className="text-xs text-accent">{stats?.social?.announcements || 0} total announcements</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function ComplianceWidget() {
  const { stats, loading } = useDashboardStats();
  
  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full" data-testid="compliance-widget">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
          <FileCheck className="w-5 h-5 text-purple-400" />
        </div>
        <h3 className="font-bold">Upcoming Events</h3>
      </div>
      {loading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-12 bg-secondary/40 rounded-xl"></div>
          <div className="h-12 bg-secondary/40 rounded-xl"></div>
        </div>
      ) : (
        <div className="space-y-2">
          {stats?.upcoming_events_list?.length > 0 ? (
            stats.upcoming_events_list.map((event, idx) => (
              <div key={idx} className="p-3 rounded-xl bg-secondary/40">
                <p className="text-sm font-medium">{event.title}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Calendar className="w-3 h-3 text-accent" />
                  <span className="text-xs text-muted-foreground">
                    {new Date(event.start_date).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-muted-foreground text-sm">
              No upcoming events
            </div>
          )}
          <div className="text-center pt-2">
            <span className="text-xs text-accent">{stats?.social?.upcoming_events || 0} events this week</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function BadgesWidget() {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const res = await axios.get(`${API}/gamification/badges`, { withCredentials: true });
        setBadges(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to fetch badges:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBadges();
  }, []);

  const safeBadges = Array.isArray(badges) ? badges : [];
  const earnedBadges = safeBadges.filter(b => b.earned);
  const inProgressBadges = safeBadges.filter(b => !b.earned && b.progress > 0).slice(0, 2);

  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full" data-testid="badges-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center">
            <Award className="w-5 h-5 text-pink-400" />
          </div>
          <h3 className="font-bold">My Badges</h3>
        </div>
        <span className="text-xs bg-pink-500/20 text-pink-400 px-2.5 py-1 rounded-full font-medium">
          {earnedBadges.length} earned
        </span>
      </div>
      {loading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-12 bg-secondary/40 rounded-xl"></div>
          <div className="h-12 bg-secondary/40 rounded-xl"></div>
        </div>
      ) : (
        <div className="space-y-3">
          {earnedBadges.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {earnedBadges.slice(0, 6).map(badge => (
                <div key={badge.badge_id} className="flex items-center gap-1 px-2 py-1 bg-pink-500/10 border border-pink-500/20 rounded-lg" title={badge.description}>
                  <span className="text-lg">{badge.icon}</span>
                  <span className="text-xs font-medium">{badge.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-2 text-muted-foreground text-sm">
              Start earning badges!
            </div>
          )}
          {inProgressBadges.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">In Progress:</p>
              {inProgressBadges.map(badge => (
                <div key={badge.badge_id} className="p-2 rounded-lg bg-secondary/30">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs flex items-center gap-1">
                      <span>{badge.icon}</span> {badge.name}
                    </span>
                    <span className="text-xs text-muted-foreground">{badge.progress}/{badge.target}</span>
                  </div>
                  <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-pink-500 rounded-full" style={{ width: `${(badge.progress / badge.target) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function StreakWidget() {
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStreak = async () => {
      try {
        await axios.post(`${API}/gamification/update-streak`, {}, { withCredentials: true });
        const res = await axios.get(`${API}/gamification/streak`, { withCredentials: true });
        setStreak(res.data);
      } catch (err) {
        console.error('Failed to fetch streak:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStreak();
  }, []);

  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full" data-testid="streak-widget">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
          <Zap className="w-5 h-5 text-orange-400" />
        </div>
        <h3 className="font-bold">Login Streak</h3>
      </div>
      {loading ? (
        <div className="animate-pulse">
          <div className="h-20 bg-secondary/40 rounded-xl"></div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-center p-4 bg-gradient-to-br from-orange-500/20 to-red-500/10 rounded-2xl border border-orange-500/30">
            <p className="text-4xl font-bold text-orange-400">{streak?.current_streak || 0}</p>
            <p className="text-sm text-orange-400/70">Day Streak</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="text-center p-2 bg-secondary/30 rounded-lg">
              <p className="text-lg font-bold text-accent">{streak?.longest_streak || 0}</p>
              <p className="text-xs text-muted-foreground">Best Streak</p>
            </div>
            <div className="text-center p-2 bg-secondary/30 rounded-lg">
              <p className="text-lg font-bold text-green-400">{streak?.current_streak >= 7 ? '✓' : `${7 - (streak?.current_streak || 0)}`}</p>
              <p className="text-xs text-muted-foreground">{streak?.current_streak >= 7 ? 'Perfect Week!' : 'Days to Badge'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
